import { Amplify } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";
import { v4 as uuidv4 } from "uuid";
import outputs from "amplify_outputs.json";
import type { Schema } from "schema/data/resource";
import { generateClient } from "aws-amplify/data";

Amplify.configure(outputs);

const client = generateClient<Schema>({
  authMode: "apiKey",
});

class InquiryService {
  private async getAuthInfo() {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken;
    const token = idToken?.toString();
    const userId = session.tokens?.idToken?.payload?.sub as string;
    const isAuthenticated = !!token;

    const domainRaw = idToken?.payload?.["custom:domain"];
    const domain = typeof domainRaw === "string" ? domainRaw : "";

    const userTypeRaw = idToken?.payload?.["custom:userType"];
    const userType = typeof userTypeRaw === "string" ? userTypeRaw : "";
    const isPartner = userType === "PARTNER";

    return { idToken, token, userId, isAuthenticated, domain, isPartner };
  }

  async createInquiry(input: any): Promise<any> {
    try {
      const { token, userId, isAuthenticated } = await this.getAuthInfo();

      const inquiryId = uuidv4();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 30);

      const { data, errors } = await client.models.Inquiry.create(
        {
          inquiryId,
          ...input,
          status: "ACTIVE",
          expiresAt: expiresAt.toISOString(),
          ...(isAuthenticated &&
            userId && {
              owner: userId,
              linkedAt: new Date().toISOString(),
            }),
        },
        {
          authMode: isAuthenticated ? "userPool" : "apiKey",
          ...(isAuthenticated && { authToken: token }),
        },
      );

      if (errors) {
        console.error("Error creating enquiry.");
        throw new Error("Failed to create enquiry");
      }

      return data;
    } catch (error) {
      console.error("Error creating enquiry:", error);
      throw error;
    }
  }

  async getAllInquiries(): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Inquiry.list({
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error("Error fetching enquiries.");
        throw new Error("Failed to fetch enquiries");
      }

      return data;
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      throw error;
    }
  }

  async getInquiriesByInstitutionAgent(
    id: string,
    status?: "ACTIVE" | "RESPONDED" | "CONVERTED" | "CLOSED",
  ): Promise<any> {
    try {
      const { token, isPartner } = await this.getAuthInfo();

      const { data, errors } = await client.models.Inquiry.list({
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error("Error fetching enquiries.");
        throw new Error("Failed to fetch enquiries");
      }

      let filtered =
        data?.filter((inquiry) =>
          isPartner ? inquiry.assignedTo === id : inquiry.institutionId === id,
        ) || [];

      if (status) {
        filtered = filtered.filter((inquiry) => inquiry.status === status);
      }

      return filtered;
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      throw error;
    }
  }

  async getInquiryById(id: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Inquiry.get(
        {
          inquiryId: id,
        },
        {
          authMode: "userPool",
          authToken: token,
          selectionSet: [
            "inquiryId",
            "email",
            "courseId",
            "institutionId",
            "firstName",
            "lastName",
            "phone.*",
            "courseName",
            "institution",
            "country",
            "source",
            "status",
            "expiresAt",
            "assignedTo",
            "assignedAt",
            "responseNotes",
            "linkedApplicationId",
            "convertedAt",
            "linkedAt",
            "owner",
            "createdAt",
            "updatedAt",
            "notes.*",
          ],
        },
      );

      if (errors) {
        console.error("Error fetching enquiry.");
        throw new Error("Failed to fetch enquiry");
      }

      return data;
    } catch (error) {
      console.error("Error fetching enquiry.");
      throw error;
    }
  }

  async updateInquiry(
    inquiryId: string,
    updateFields: Record<string, any>,
  ): Promise<any> {
    try {
      const { token, isPartner } = await this.getAuthInfo();

      const allowedPartnerFields = ["status", "responseNotes"];
      const safeUpdate: Record<string, any> = {};

      if (isPartner) {
        for (const key of allowedPartnerFields) {
          if (updateFields[key] !== undefined) {
            safeUpdate[key] = updateFields[key];
          }
        }
      } else {
        for (const key in updateFields) {
          if (key !== "createdAt" && key !== "updatedAt") {
            safeUpdate[key] = updateFields[key];
          }
        }
      }

      const { data, errors } = await client.models.Inquiry.update(
        {
          inquiryId,
          ...safeUpdate,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error updating enquiry.");
        throw new Error("Failed to update enquiry");
      }

      return data;
    } catch (error) {
      console.error("Error updating enquiry.");
      throw error;
    }
  }

  async markAsConverted(
    inquiryId: string,
    applicationId: string,
  ): Promise<any> {
    try {
      return await this.updateInquiry(inquiryId, {
        status: "CONVERTED",
        linkedApplicationId: applicationId,
        convertedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error marking enquiry as converted.");
      throw error;
    }
  }

  async addNote(id: string, inquiry: any, content: string) {
    try {
      const { token } = await this.getAuthInfo();
      const noteId = uuidv4();

      const result = await client.models.InquiryNote.create(
        {
          noteId,
          inquiryId: id,
          assignedTo: inquiry.assignedTo || null,
          institutionId: inquiry.institutionId || null,
          content,
        },
        {
          authMode: "userPool",
          authToken: token,
        },
      );
      return result;
    } catch (err) {
      console.error("Error adding enquiry note:", err);
      throw err;
    }
  }

  async removeNote(noteId: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const data = await client.models.InquiryNote.delete(
        { noteId },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      return data;
    } catch (error) {
      console.error("Error removing enquiry note:", error);
      throw error;
    }
  }

  async assignAgent(inquiryId: string, agentId: string): Promise<any> {
    try {
      const { isPartner } = await this.getAuthInfo();

      if (isPartner) {
        throw new Error("Partners cannot assign enquiries.");
      }

      return await this.updateInquiry(inquiryId, {
        assignedTo: agentId,
        assignedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error assigning agent to enquiry:", error);
      throw error;
    }
  }

  async deleteInquiry(inquiryId: string): Promise<any> {
    try {
      return await this.updateInquiry(inquiryId, {
        status: "CLOSED",
      });
    } catch (error) {
      console.error("Error deleting enquiry:", error);
      throw error;
    }
  }

  async hardDeleteInquiry(inquiryId: string): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Inquiry.delete(
        { inquiryId },
        {
          authMode: "userPool",
          authToken: token,
        },
      );

      if (errors) {
        console.error("Error hard deleting enquiry.");
        throw new Error("Failed to hard delete enquiry");
      }

      return data;
    } catch (error) {
      console.error("Error hard deleting enquiry:", error);
      throw error;
    }
  }

  async convertUserInquiries(
    userId: string,
    applicationId: string,
  ): Promise<any> {
    try {
      const { token } = await this.getAuthInfo();

      const { data, errors } = await client.models.Inquiry.list({
        authMode: "userPool",
        authToken: token,
      });

      if (errors) {
        console.error("Error fetching enquiries.");
        throw new Error("Failed to fetch enquiries");
      }

      const inquiries =
        data?.filter(
          (inquiry) =>
            inquiry.owner === userId &&
            (inquiry.status === "ACTIVE" || inquiry.status === "RESPONDED"),
        ) || [];

      if (inquiries.length === 0) {
        return { convertedCount: 0 };
      }

      await Promise.all(
        inquiries.map((inquiry) =>
          this.markAsConverted(inquiry.inquiryId, applicationId),
        ),
      );

      return { convertedCount: inquiries.length };
    } catch (error) {
      console.error("Error converting user enquiries:", error);
      throw error;
    }
  }
}

export const inquiryService = new InquiryService();
